import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import { useAuth } from '../auth'
import { formatDate, plural } from '../lib/format'
import TicketDialog, { STATUS_CLASS } from '../components/TicketDialog'
import { useEscClose } from '../lib/useEscClose'
import {
  CATEGORIES_OF,
  TICKET_CATEGORY_LABEL,
  TICKET_STATUS_LABEL,
  type TicketCategory,
  type TicketKind,
} from '../types'

/** Справочники разового обращения. Совпадают с app/ticket_rules.py: там же
 *  их и проверяют, здесь они нужны только чтобы нарисовать выбор. */
const SINGLE_PROGRAMS = ['1С', 'Клеверенс', 'Бизера']
const SINGLE_DIRECTIONS = ['Честный знак', 'Программа', 'Оборудование']

/**
 * Обращения клиентов из кабинета.
 *
 * Отдельный экран, а не вкладка журнала: обращение — не задача. Из одного
 * обращения может вырасти три задачи на разных людей, а три мелких вопроса
 * закрыться одной. Связь видна в карточке обращения.
 */
export default function Tickets({ kind }: { kind: TicketKind }) {
  // Пришли из переписки по ссылке «Открыть заявку» — сразу раскрываем карточку
  const [search, setSearch] = useSearchParams()
  const { can } = useAuth()
  const [openOnly, setOpenOnly] = useState(true)
  const [openId, setOpenId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [category, setCategory] = useState<TicketCategory | ''>('')

  // Раздел сменился — фильтр по виду сбрасываем: вид из другого раздела дал бы
  // пустой список, и человек решил бы, что записи пропали.
  useEffect(() => {
    setCategory('')
  }, [kind])

  useEffect(() => {
    const open = search.get('open')
    if (!open) return
    setOpenId(Number(open))
    // Убираем из адреса, иначе карточка снова раскроется при возврате назад
    search.delete('open')
    setSearch(search, { replace: true })
  }, [search, setSearch])

  const заявки = kind === 'order'

  const list = useQuery({
    queryKey: ['tickets', kind, openOnly, category],
    queryFn: () =>
      api.tickets({
        kind,
        open_only: openOnly || undefined,
        category: category || undefined,
      }),
    // Клиент мог написать минуту назад — список обновляется сам
    refetchInterval: 60_000,
  })

  const rows = list.data ?? []
  const waiting = rows.filter((t) => t.waiting_for_us).length

  return (
    <>
      <h1 className="page-title">{заявки ? 'Заявки' : 'Обращения'}</h1>

      <div className="toolbar">
        {can('tickets.reply') && (
          <button className="btn primary" onClick={() => setCreating(true)}>
            {заявки ? '+ Новая заявка' : '+ Новое обращение'}
          </button>
        )}
        <button
          className={`btn${openOnly ? ' primary' : ''}`}
          onClick={() => setOpenOnly(!openOnly)}
        >
          {openOnly ? '✓ Только открытые' : 'Только открытые'}
        </button>
        <select
          className="btn"
          value={category}
          onChange={(e) => setCategory(e.target.value as TicketCategory | '')}
          title="Показать только один вид обращений"
        >
          <option value="">Все виды</option>
          {CATEGORIES_OF[kind].map((c) => (
            <option key={c} value={c}>
              {TICKET_CATEGORY_LABEL[c]}
            </option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        <div style={{ color: 'var(--muted)', paddingBottom: 6 }}>
          {waiting > 0 && (
            <strong style={{ color: '#b91c1c' }}>Ждут ответа: {waiting} · </strong>
          )}
          Всего: {rows.length}
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="col-num">№</th>
              <th>Клиент</th>
              <th>Вид</th>
              <th>Тема</th>
              <th>Кто обращался</th>
              <th>Состояние</th>
              <th>Ведёт</th>
              <th>Обновлено</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr>
                <td colSpan={9} className="empty">
                  Загрузка…
                </td>
              </tr>
            )}
            {!list.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="empty">
                  {openOnly ? 'Открытых обращений нет' : 'Обращений пока не было'}
                </td>
              </tr>
            )}
            {rows.map((t) => (
              <tr key={t.id} className={t.waiting_for_us ? 'no-estimate' : undefined}>
                <td className="task-num">{t.id}</td>
                <td className="cell-client">
                  <span className="client-name">{t.client_name}</span>
                </td>
                <td className="nowrap">
                  <span className="tk-cat-chip">{t.category_title}</span>
                  {t.codes_count !== null && (
                    <span className="cell-sub">
                      {t.codes_count} {plural(t.codes_count, 'код', 'кода', 'кодов')}
                    </span>
                  )}
                  {t.direction && t.category !== 'codes' && (
                    <span className="cell-sub">{t.direction}</span>
                  )}
                </td>
                <td>
                  <button className="link-btn" onClick={() => setOpenId(t.id)}>
                    {t.subject}
                  </button>
                  {t.waiting_for_us && (
                    <span className="cell-sub" style={{ color: '#b91c1c', fontWeight: 700 }}>
                      ждёт ответа
                    </span>
                  )}
                  {t.files_count > 0 && (
                    <span className="cell-sub">файлов: {t.files_count}</span>
                  )}
                </td>
                <td>{t.author_name ?? '—'}</td>
                <td>
                  <span className={`tk-chip ${STATUS_CLASS[t.status]}`}>
                    {TICKET_STATUS_LABEL[t.status]}
                  </span>
                  {t.stage_title && <span className="cell-sub">{t.stage_title}</span>}
                  {t.agreed_hours !== null && (
                    <span className="cell-sub">согласовано {t.agreed_hours} ч</span>
                  )}
                  {t.paid && <span className="cell-sub">оплачено</span>}
                  {t.overdue && (
                    <span className="cell-sub" style={{ color: '#b91c1c', fontWeight: 700 }}>
                      срок прошёл
                    </span>
                  )}
                </td>
                <td>{t.assignee_name ?? '—'}</td>
                <td className="nowrap">{formatDate(t.updated_at)}</td>
                <td className="nowrap">
                  <button className="btn small" onClick={() => setOpenId(t.id)}>
                    Открыть
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <NewTicketDialog kind={kind} onClose={() => setCreating(false)} onCreated={setOpenId} />
      )}

      {openId !== null && (
        <TicketDialog
          id={openId}
          mayReply={can('tickets.reply')}
          onClose={() => setOpenId(null)}
        />
      )}
    </>
  )
}

/**
 * Обращение, заведённое нами: клиент позвонил, а не написал.
 *
 * Автора со стороны клиента указываем, если человек заведён в кабинете —
 * тогда он увидит обращение у себя и получит письмо с ответом. Если нет,
 * обращение всё равно живёт, просто без адресата для уведомлений.
 */
function NewTicketDialog({
  kind,
  onClose,
  onCreated,
}: {
  kind: TicketKind
  onClose: () => void
  onCreated: (id: number) => void
}) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    client_id: '',
    author_portal_id: '',
    subject: '',
    description: '',
    category: CATEGORIES_OF[kind][0],
    program: '',
    direction: '',
  })
  const [error, setError] = useState('')

  useEscClose(onClose)

  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.clients() })

  // Список сотрудников кабинета доступен не всем: у специалиста может не быть
  // права на клиентов. Тогда просто не показываем выбор автора.
  const portalUsers = useQuery({
    queryKey: ['portal-users', form.client_id],
    queryFn: () => api.portalUsers(Number(form.client_id)),
    enabled: !!form.client_id,
    retry: false,
  })

  const create = useMutation({
    mutationFn: () =>
      api.createTicket({
        client_id: Number(form.client_id),
        author_portal_id: form.author_portal_id ? Number(form.author_portal_id) : null,
        subject: form.subject.trim(),
        description: form.description.trim() || null,
        category: form.category,
        program: form.program || null,
        direction: form.direction || null,
      }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['tickets'] })
      onClose()
      onCreated(t.id)
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось создать'),
  })

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value, ...(key === 'client_id' ? { author_portal_id: '' } : {}) })

  const authors = (portalUsers.data ?? []).filter((u) => u.is_active)
  const ready =
    form.client_id &&
    form.subject.trim() &&
    (form.category !== 'work' || (form.program && form.direction))

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="modal"
        style={{ maxWidth: 560 }}
        onSubmit={(e) => {
          e.preventDefault()
          setError('')
          create.mutate()
        }}
      >
        <h2>{kind === 'order' ? 'Новая заявка' : 'Новое обращение'}</h2>
        <div className="modal-body">
          {error && <div className="error">{error}</div>}
          <p className="hint">
            Для звонков и писем мимо кабинета: заводим сами, чтобы переписка
            и состояние были в одном месте.
          </p>

          <div className="field full field-accent">
            <label htmlFor="st-client">Клиент</label>
            <select id="st-client" value={form.client_id} onChange={set('client_id')} required autoFocus>
              <option value="">— выберите —</option>
              {clients.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {form.client_id && authors.length > 0 && (
            <div className="field full">
              <label htmlFor="st-author">Кто обратился</label>
              <select id="st-author" value={form.author_portal_id} onChange={set('author_portal_id')}>
                <option value="">— не из кабинета —</option>
                {authors.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                    {u.position ? ` · ${u.position}` : ''}
                  </option>
                ))}
              </select>
              <span className="hint-inline">
                Укажите — и человек увидит обращение в кабинете, а ответы получит письмом
              </span>
            </div>
          )}

          <div className="field full">
            <label htmlFor="st-cat">{kind === 'order' ? 'Вид заявки' : 'Вид обращения'}</label>
            <select
              id="st-cat"
              value={form.category}
              onChange={(e) =>
                setForm({
                  ...form,
                  category: e.target.value as TicketCategory,
                  program: '',
                  direction: '',
                })
              }
            >
              {CATEGORIES_OF[kind].map((c) => (
                <option key={c} value={c}>
                  {TICKET_CATEGORY_LABEL[c]}
                </option>
              ))}
            </select>
            <span className="hint-inline">
              От вида зависит, кто из наших это увидит и сможет взять в работу
            </span>
          </div>

          {form.category === 'work' && (
            <div className="grid-2">
              <div className="field">
                <label htmlFor="st-prog">Программа</label>
                <select id="st-prog" value={form.program} onChange={set('program')} required>
                  <option value="">— выберите —</option>
                  {SINGLE_PROGRAMS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="st-dir">Направление</label>
                <select id="st-dir" value={form.direction} onChange={set('direction')} required>
                  <option value="">— выберите —</option>
                  {SINGLE_DIRECTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {(form.category === 'codes' || form.category === 'rent_1c') && (
            <div className="warn">
              {form.category === 'codes'
                ? 'Коды заводятся из кабинета клиента: там он выбирает товарную группу и прикладывает заполненный шаблон, по которому считается количество. Записанный звонок оформите разовым обращением.'
                : 'Аренда 1С заводится из кабинета: база подставляется из доступов клиента. Записанный звонок оформите разовым обращением.'}
            </div>
          )}

          <div className="field full">
            <label htmlFor="st-subj">Тема</label>
            <input
              id="st-subj"
              value={form.subject}
              onChange={set('subject')}
              placeholder="Звонок: не открывается касса"
              required
            />
          </div>

          <div className="field full">
            <label htmlFor="st-desc">Что случилось</label>
            <textarea id="st-desc" rows={4} value={form.description} onChange={set('description')} />
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button className="btn primary" disabled={create.isPending || !ready}>
            {create.isPending ? 'Создаём…' : 'Создать'}
          </button>
        </div>
      </form>
    </div>
  )
}
